import { QueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import type { TFunction } from "i18next"
import { SegmentGroupDetail } from "modules/segments/components/SegmentGroupDetail"
import { getPredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { getSegmentGroupQueryKey, segmentGroupQuery } from "modules/segments/lib/segmentGroupQuery"
import { Crumb } from "modules/shared/lib/breadcrumbs"
import { SegmentGroupResponseDataSchema } from "schema"
import z from "zod"

function getSegmentGroupName(id: string, queryClient: QueryClient, t: TFunction) {
  const predefinedSegmentGroupsTranslation = getPredefinedSegmentGroupsTranslation(t)

  const segmentGroup = queryClient.getQueryData(getSegmentGroupQueryKey(id)) as z.infer<
    typeof SegmentGroupResponseDataSchema
  >
  if (segmentGroup) {
    return (
      (segmentGroup.isPredefined &&
        predefinedSegmentGroupsTranslation[
          segmentGroup.name as keyof typeof predefinedSegmentGroupsTranslation
        ]) ||
      segmentGroup.name
    )
  }

  return queryClient.fetchQuery(segmentGroupQuery({ id })).then((segmentGroup) => {
    return (
      (segmentGroup.isPredefined &&
        predefinedSegmentGroupsTranslation[
          segmentGroup.name as keyof typeof predefinedSegmentGroupsTranslation
        ]) ||
      segmentGroup.name
    )
  })
}

const SearchSchema = z.object({
  view: z.enum(["chart", "table"]).default("chart"),
  compareId: z.string().default("none"),
})

export const Route = createFileRoute("/_base/_auth/customers/segments/$id")({
  component: RouteComponent,
  loader: async ({ context, route: { fullPath }, params }) => {
    return new Crumb({
      fullPath,
      title: (t) => getSegmentGroupName(params.id, context.queryClient, t),
    })
  },
  validateSearch: SearchSchema,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  function onChangeView(view: (typeof search)["view"]) {
    navigate({ search: { ...search, view } })
  }

  function onChangeCompareId(compareId: string | undefined) {
    navigate({ search: { ...search, compareId } })
  }

  return (
    <SegmentGroupDetail
      segmentGroupId={id}
      compareId={search.compareId}
      view={search.view}
      onChangeView={onChangeView}
      onChangeCompareId={onChangeCompareId}
    />
  )
}
